import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as yup from 'yup';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';

interface ICountryContext {
    isLoading: boolean
    country: Country
    isSaving: boolean

    saveCountry: (country: Country) => void
}

export let countrySchema = yup.object({
    id: yup.string().nullable().default(null),
    code: yup.string().required().default('').label('Code'),
    name: yup.string().required().default('').label('Name'),
    currencyCode: yup.string().required().default('').label('Currency code'),
    currencySymbol: yup.string().required().default('').label('Currency symbol'),
    currencyDecimal: yup.number().required().default(0).label('Currency decimal'),
    overrideTaxRate: yup.boolean().default(false).label('Override tax rate'),
    taxRateOverride: yup.string().default('')
        .when("overrideTaxRate", {
            is: true,
            then: yup.string().required().default('').label('Override tax rate'),
            otherwise: yup.string().default('').nullable().label('Override tax rate'),
        }),
    locales: yup.array().of(yup.string().default('')).default([]).label("Locale"),
});

type Country = yup.InferType<typeof countrySchema>;

export let countryContext = React.createContext({} as ICountryContext);

let { Provider } = countryContext;

let CountryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    let { id } = useParams<{ id: string }>();
    let location = useLocation();
    let navigate = useNavigate();
    let { enqueueSnackbar } = useSnackbar();

    let isNew = location.pathname.includes("/addnew");

    let [isLoading, setLoading] = useState<boolean>(isNew === false);
    let [country, setCountry] = useState<Country>(countrySchema.getDefault());
    let [isSaving, setSaving] = useState(false);


    useEffect(() => {
        if (isNew === false)
            getCountry();

    }, [id, isNew])

    let getCountry = async () => {
        try {
            let res = await axios.get('/api/Country/GetCountry', { params: { id } });

            let { item } = res.data;
            setCountry({
                ...item,
                overrideTaxRate: item.taxRateOverride ? true : false
            });
            setLoading(false);

        } catch (err) { }
        finally {
        }
    }

    let saveCountry = async (value: Country) => {
        try {
            setSaving(true);

            await axios.post('/api/Country/SaveCountry', value);
            enqueueSnackbar("Country saved", { variant: "success" });
            navigate(`/admin/country`);

        } catch (err) {
            setSaving(false);
        }
    }

    return (
        <Provider
            value={{
                isLoading,
                country,
                isSaving,

                saveCountry
            }}
        >
            {children}
        </Provider>
    )
}

export default CountryProvider;